import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { separationApi } from '../../api/separation';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';

const SEPARATION_TYPE_LABELS: Record<string, string> = { resignation: 'Resignation', end_of_contract: 'End of Contract', termination: 'Termination', redundancy: 'Redundancy', retirement: 'Retirement', other: 'Other' };
const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft', approved: 'Approved', rejected: 'Rejected', completed: 'Completed', cancelled: 'Cancelled' };
const CLEARANCE_LABELS: Record<string, string> = { supervisor: 'Supervisor', hr: 'HR', finance: 'Finance', it: 'IT', asset_return: 'Asset Return', document_return: 'Document Return' };

export function SeparationPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const allQuery = useQuery({ queryKey: ['separation-all'], queryFn: () => separationApi.listAll() });
  const detailQuery = useQuery({ queryKey: ['separation-detail', selectedId], queryFn: () => separationApi.getById(selectedId!), enabled: !!selectedId });

  const { register, handleSubmit, reset } = useForm<{ employeeId: string; separationType: string; lastWorkingDay: string; reason: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { employeeId: string; separationType: string; lastWorkingDay: string; reason: string }) =>
      separationApi.create({ employeeId: Number(v.employeeId), separationType: v.separationType, lastWorkingDay: v.lastWorkingDay, reason: v.reason || undefined }),
    onSuccess: (result) => { showSuccess(`Separation case created - ${result.data.separationReference}`); queryClient.invalidateQueries({ queryKey: ['separation-all'] }); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create separation case.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <div className="page-header">
        <h1>Separation &amp; Exit</h1>
        <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Start Separation'}</button>
      </div>

      {showForm && (
        <section className="card">
          <h2>Start Separation</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Employee ID<input type="number" {...register('employeeId', { required: true })} /></label>
            <label>
              Separation Type
              <select {...register('separationType', { required: true })}>
                <option value="">- select -</option>
                {Object.entries(SEPARATION_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>Last Working Day<input type="date" {...register('lastWorkingDay', { required: true })} /></label>
            <label>Reason<textarea rows={2} {...register('reason')} /></label>
            <div className="form-actions"><button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button></div>
          </form>
        </section>
      )}

      <section className="card">
        <h2>All Separation Cases</h2>
        {allQuery.data && (
          <DataTable
            rows={allQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No separation cases yet."
            columns={[
              { header: 'Reference', render: (r) => r.separation_reference },
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Type', render: (r) => SEPARATION_TYPE_LABELS[r.separation_type] ?? r.separation_type },
              { header: 'Last Working Day', render: (r) => r.last_working_day },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => <button className="link-button" onClick={() => setSelectedId(r.id)}>Open</button> },
            ]}
          />
        )}
      </section>

      {selectedId && detailQuery.data && <SeparationWorkspace id={selectedId} data={detailQuery.data} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function SeparationWorkspace({ id, data, onClose }: { id: number; data: { data: import('../../api/separation').SeparationCase; clearances: import('../../api/separation').SeparationClearance[] }; onClose: () => void }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const record = data.data;

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: ['separation-detail', id] }); queryClient.invalidateQueries({ queryKey: ['separation-all'] }); };

  const submitMutation = useMutation({ mutationFn: () => separationApi.submit(id), onSuccess: () => { showSuccess('Submitted.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decisionMutation = useMutation({
    mutationFn: (v: { decision: 'approved' | 'rejected'; comment: string; effectiveSeparationDate?: string }) => separationApi.decide(id, v.decision, v.comment, v.effectiveSeparationDate),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const clearanceMutation = useMutation({
    mutationFn: ({ clearanceId, status }: { clearanceId: number; status: 'cleared' | 'flagged' }) => separationApi.recordClearance(clearanceId, status),
    onSuccess: () => { showSuccess('Clearance recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed - you may not be authorised for this clearance.'),
  });
  const balanceQuery = useQuery({ queryKey: ['separation-balance', id], queryFn: () => separationApi.computeLeaveBalance(id), enabled: record.status === 'approved' });
  const finalFiguresForm = useForm<{ leaveBalanceDays: string; salaryAmount: string; currency: string }>();
  const finalFiguresMutation = useMutation({
    mutationFn: (v: { leaveBalanceDays: string; salaryAmount: string; currency: string }) => separationApi.recordFinalFigures(id, { leaveBalanceDays: Number(v.leaveBalanceDays), salaryAmount: v.salaryAmount ? Number(v.salaryAmount) : undefined, currency: v.currency || undefined }),
    onSuccess: () => { showSuccess('Final figures recorded.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({ mutationFn: () => separationApi.complete(id), onSuccess: () => { showSuccess('Separation completed.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed - check every clearance is resolved.') });

  const allCleared = data.clearances.every((c) => c.status !== 'pending');

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="page-header">
        <h2>{record.separation_reference} - {STATUS_LABELS[record.status] ?? record.status}</h2>
        <button className="link-button" onClick={onClose}>Close Workspace</button>
      </div>
      <p className="muted">{record.employee_name} · {SEPARATION_TYPE_LABELS[record.separation_type]} · Last Working Day: {record.last_working_day}</p>
      {record.reason && <p>{record.reason}</p>}

      {record.status === 'draft' && <div className="form-actions"><button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>Submit</button></div>}

      {(record.status === 'submitted' || record.status === 'under_review') && (
        <div className="form-actions">
          <button onClick={() => { const c = window.prompt('Approval comment:'); const d = window.prompt('Effective separation date (YYYY-MM-DD):'); if (c) decisionMutation.mutate({ decision: 'approved', comment: c, effectiveSeparationDate: d ?? undefined }); }}>Approve</button>
          <button onClick={() => { const c = window.prompt('Rejection reason:'); if (c) decisionMutation.mutate({ decision: 'rejected', comment: c }); }}>Reject</button>
          <ReturnAction onReturn={(comment) => separationApi.return(id, comment)} queryKeysToInvalidate={[['separation-detail', id], ['separation-all']]} />
        </div>
      )}

      {(record.status === 'approved' || record.status === 'completed') && (
        <>
          <h3>Clearance Checklist</h3>
          <DataTable
            rows={data.clearances}
            rowKey={(c) => c.id}
            emptyMessage="No clearance items."
            columns={[
              { header: 'Type', render: (c) => CLEARANCE_LABELS[c.clearance_type] ?? c.clearance_type },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
              { header: 'Cleared By', render: (c) => c.cleared_by_name ?? '-' },
              {
                header: '',
                render: (c) => c.status === 'pending' && record.status === 'approved' && (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => clearanceMutation.mutate({ clearanceId: c.id, status: 'cleared' })}>Clear</button>
                    <button className="link-button" onClick={() => clearanceMutation.mutate({ clearanceId: c.id, status: 'flagged' })}>Flag</button>
                  </span>
                ),
              },
            ]}
          />
        </>
      )}

      {record.status === 'approved' && (
        <>
          <h3>Final Figures</h3>
          {balanceQuery.data && <p className="muted">Computed leave balance reference: {balanceQuery.data.data.computedBalance} days ({balanceQuery.data.data.year})</p>}
          <form onSubmit={finalFiguresForm.handleSubmit((v) => finalFiguresMutation.mutate(v))}>
            <label>Final Leave Balance (days)<input type="number" step="0.5" {...finalFiguresForm.register('leaveBalanceDays', { required: true })} /></label>
            <label>Final Salary Amount<input type="number" {...finalFiguresForm.register('salaryAmount')} /></label>
            <label>Currency<input {...finalFiguresForm.register('currency')} placeholder="NGN" /></label>
            <div className="form-actions"><button type="submit" disabled={finalFiguresMutation.isPending}>Record Final Figures</button></div>
          </form>

          <h3>Exit Interview</h3>
          {record.exit_interview_completed ? <p className="muted">Completed: {record.exit_interview_notes}</p> : (
            <button onClick={() => { const n = window.prompt('Exit interview notes:'); if (n) separationApi.recordExitInterview(id, n).then(invalidate); }}>Record Exit Interview</button>
          )}

          <div className="form-actions" style={{ marginTop: 16 }}>
            <button onClick={() => completeMutation.mutate()} disabled={!allCleared || completeMutation.isPending}>Complete Separation</button>
            {!allCleared && <span className="muted"> All clearances must be resolved first.</span>}
          </div>
        </>
      )}

      {record.final_salary_amount && <p className="muted">Final salary paid: {record.final_salary_currency} {Number(record.final_salary_amount).toLocaleString()}</p>}
      {record.decision_comment && <p className="muted">Decision: {record.decision_comment}</p>}
    </section>
  );
}
